import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Users, Award, Headphones, Target, BookOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function About() {
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const features = [
    {
      icon: Award,
      title: t('about.features.certified'),
      description: t('about.features.certifiedDesc'),
    },
    {
      icon: Users,
      title: t('about.features.multilingual'),
      description: t('about.features.multilingualDesc'),
    },
    {
      icon: Shield,
      title: t('about.features.practical'),
      description: t('about.features.practicalDesc'),
    },
    {
      icon: Headphones,
      title: t('about.features.support'),
      description: t('about.features.supportDesc'),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="about-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('about.title')}
          </h1>
          <p className="text-gray-600 max-w-2xl">
            {t('aboutPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Target className="h-6 w-6 text-[#00A651]" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {t('about.mission')}
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                {language === 'ja' ? (settings?.mission || t('about.missionText')) : t('about.missionText')}
              </p>
              <div className="bg-[#e6f6ec] border-l-4 border-[#00A651] p-6 rounded-r-lg">
                <p className="text-gray-700 font-medium">
                  {t('aboutPage.quote')}
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760009436767-d154e930e55c?w=800&auto=format&fit=crop"
                alt="Construction safety"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#00A651] text-white p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">{t('home.graduatesMany')}</p>
                <p className="text-sm">
                  {t('home.statsGraduates')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('about.services')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-lg border-l-4 border-[#00A651] shadow-sm hover:shadow-md transition-shadow"
                data-testid={`about-feature-${index}`}
              >
                <feature.icon className="h-10 w-10 text-[#00A651] mb-4" />
                <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organization Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <BookOpen className="h-6 w-6 text-[#00A651]" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              {t('aboutPage.orgOverview')}
            </h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-8">
            <table className="w-full">
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 pr-4 font-medium text-gray-900 w-1/3">
                    {t('aboutPage.orgNameLabel')}
                  </td>
                  <td className="py-4 text-gray-600">
                    {language === 'ja' ? (settings?.organizationName || t('aboutPage.orgNameDefault')) : (settings?.organizationNameEn || t('aboutPage.orgNameDefault'))}
                  </td>
                </tr>
                {settings?.representative && (
                  <tr>
                    <td className="py-4 pr-4 font-medium text-gray-900">{t('aboutPage.representative')}</td>
                    <td className="py-4 text-gray-600">{settings.representative}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-4 pr-4 font-medium text-gray-900">
                    {t('aboutPage.established')}
                  </td>
                  <td className="py-4 text-gray-600">
                    {language === 'ja' ? (settings?.establishedDate || t('aboutPage.establishedYear')) : t('aboutPage.establishedYear')}
                  </td>
                </tr>
                <tr>
                  <td className="py-4 pr-4 font-medium text-gray-900">
                    {t('aboutPage.location')}
                  </td>
                  <td className="py-4 text-gray-600 whitespace-pre-line">
                    {language === 'ja' ? (settings?.address || t('aboutPage.locationDefault')) : t('aboutPage.locationDefault')}
                  </td>
                </tr>
                <tr>
                  <td className="py-4 pr-4 font-medium text-gray-900">
                    {t('aboutPage.servicesLabel')}
                  </td>
                  <td className="py-4 text-gray-600">
                    {language === 'ja' ? (settings?.description || t('aboutPage.servicesDefault')) : t('aboutPage.servicesDefault')}
                  </td>
                </tr>
                {settings?.employeeCount && (
                  <tr>
                    <td className="py-4 pr-4 font-medium text-gray-900">{t('aboutPage.employeeCount')}</td>
                    <td className="py-4 text-gray-600">{settings.employeeCount}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-4 pr-4 font-medium text-gray-900">
                    {t('aboutPage.languagesLabel')}
                  </td>
                  <td className="py-4 text-gray-600">
                    {language === 'ja' ? (settings?.supportedLanguages || t('aboutPage.languagesList')) : t('aboutPage.languagesList')}
                  </td>
                </tr>
                {settings?.orgProfileExtra && (
                  <tr>
                    <td className="py-4 pr-4 font-medium text-gray-900">{t('aboutPage.other')}</td>
                    <td className="py-4 text-gray-600 whitespace-pre-wrap">{settings.orgProfileExtra}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#00A651]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            <div>
              <p className="text-4xl lg:text-5xl font-bold font-[Barlow_Condensed]">20+</p>
              <p className="text-white/80 mt-2">
                {t('home.statsCourses')}
              </p>
            </div>
            <div>
              <p className="text-4xl lg:text-5xl font-bold font-[Barlow_Condensed]">7</p>
              <p className="text-white/80 mt-2">
                {t('home.statsLanguages')}
              </p>
            </div>
            <div>
              <p className="text-4xl lg:text-5xl font-bold">{t('home.graduatesMany')}</p>
              <p className="text-white/80 mt-2">
                {t('home.statsGraduates')}
              </p>
            </div>
            <div>
              <p className="text-4xl lg:text-5xl font-bold font-[Barlow_Condensed]">98%</p>
              <p className="text-white/80 mt-2">
                {t('home.statsPassRate')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
